import type { ProcessedCourse } from "@/common/course/types";
import type { CourseRun } from "@/common/courseRun/types";
import CourseEnrollButton from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons";
import PromoBanner from "@/components/PromoBanner";
import { PromoBannerMonarchProps } from "@/monarch/types/PromoBanner";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import { PromoBannerVariant } from "@/components/PromoBanner/types";
import {
  SLIM_V2_CTA_HEIGHT,
  SLIM_V2_SIDERAIL_CTA_HEIGHT,
} from "@/components/PromoBanner/variants/Slim/v2/constants";
import { resolveCompleteProps, resolveVariant } from "./utils";

type OCPromoBannerProps = {
  course: ProcessedCourse;
  courseRun: CourseRun;
  data: PromoBannerMonarchProps;
  variant?: PromoBannerVariant;
  /**
   * When true, the slim v2 monarch payload renders as the inverted
   * side-rail layout (`slimV2SideRail`). Set by the page layout when a
   * side-rail column is shown (e.g. `HeroSideRail` template).
   */
  sideRail?: boolean;
};

export default function OCPromoBanner({
  course,
  courseRun,
  data,
  variant = "compactPDP",
  sideRail = false,
}: OCPromoBannerProps) {
  const finalVariant = resolveVariant(data, variant, { sideRail });
  const completeProps = resolveCompleteProps(data, finalVariant);
  const courseRunPrice = getCoursePrice(courseRun);

  // Pick the right CTA sizing for each slim v2 layout. Standard slim v2
  // uses a custom tablet breakpoint (906px); the side-rail variant uses
  // the default `md` (768px). Other variants fall back to default.
  const buttonClass =
    finalVariant === "slimV2"
      ? `inline-flex ${SLIM_V2_CTA_HEIGHT} py-0 whitespace-nowrap`
      : finalVariant === "slimV2SideRail"
        ? `inline-flex ${SLIM_V2_SIDERAIL_CTA_HEIGHT} py-0 whitespace-nowrap`
        : finalVariant === "slim"
          ? "mb-4"
          : "";

  return (
    <PromoBanner
      {...data}
      {...completeProps}
      variant={finalVariant}
      ctaElement={
        <CourseEnrollButton
          course={course}
          courseRun={courseRun}
          name="promo-banner-cta"
          location="promo-banner"
          buttonClass={buttonClass}
          price={courseRunPrice}
        />
      }
    />
  );
}
